// Settings › Visibility — every retailer-facing string for the sale filters (spec
// sale-filter-self-service.md). Pure functions, no React or router, so Jest can test them.
//
// The two filters, both working both ways (the engine applies the owner's rule on both sides):
// - base:   on your full-price product, partner offers on sale are hidden; and your sale items are
//           not shown on partners' full-price products.
// - deeper: on your sale product, partner offers whose discount is more than `t` points deeper are
//           hidden; and your sale items are not shown where yours is more than `t` points deeper.
import { formatDate } from '../partnerRules/copy';

export const BASE_LABEL = 'Hide partner sale items on your full-price products';
// The dialogs state the effect plainly (they ask "Turn on …?" first); the card says what happens
// once a filter is on, so an Off switch never reads as if it were already working.
export const BASE_EFFECT =
  "Shoppers on your full-price products no longer see partners' sale items, and your sale items are no longer shown on partners' full-price products.";
export const BASE_HINT =
  "When this is on, shoppers on your full-price products do not see partners' sale items. Your sale items are then also not shown on partners' full-price products.";

export const DEEPER_LABEL = 'Also hide partner offers with a much bigger discount than yours';
const deeperEffect = (t) =>
  `shoppers on your sale products do not see partner offers with a discount more than ${t}% bigger. ` +
  `Example: you give 20% off, partners with more than ${20 + t}% off are hidden. This also works both ways.`;
export const deeperHint = (t) => `When this is on, ${deeperEffect(t)}`;
export const deeperDialog = (t) => `S${deeperEffect(t).slice(1)}`;

export const thresholdLabel = 'Much bigger means more than';
export const thresholdOption = (t) => `${t}% bigger`;

export const OFF_SUMMARY = 'This filter is off. Sale items are shown as normal, on your site and on partner sites.';

// The confirmation for a change. `from` and `to` are {base, deeper, threshold_pp}; `recent` adds the
// "changed today" line (spec requirement 5). Words only, no estimate, one or two lines; "within a
// minute" is in the success notice, not here (Daniel, 2026-10-29).
export function changeDialog(from, to, recent) {
  const lines = [];
  let title;
  if (!from.base && to.base) {
    title = 'Turn on the sale filter?';
    lines.push(`${BASE_EFFECT} This means fewer orders from partners.`);
  } else if (from.base && !to.base) {
    title = from.deeper ? 'Turn off both sale filters?' : 'Turn off the sale filter?';
    lines.push(
      `${from.deeper ? 'Both filters go off. ' : ''}Sale items are shown as normal again, on your site and on partner sites.`
    );
  } else if (!from.deeper && to.deeper) {
    title = 'Also hide much bigger discounts?';
    lines.push(`${deeperDialog(to.threshold_pp)} This means fewer orders from partners.`);
  } else if (from.deeper && !to.deeper) {
    title = 'Show much bigger discounts again?';
    lines.push('Partner offers with a much bigger discount than yours are shown again, on your site and on theirs.');
  } else {
    title = `Change "much bigger" to more than ${to.threshold_pp}%?`;
    lines.push(deeperDialog(to.threshold_pp));
  }
  if (recent) lines.push('This was changed today.');
  return { title, lines };
}

export function changedLine(changed) {
  if (!changed) return null;
  if (changed.by === 'HandOff') return `Set by HandOff on ${formatDate(changed.on)}.`;
  return `Changed on ${formatDate(changed.on)} by ${changed.by}.`;
}

// The last 30 days, from the same numbers as the Badge Fairness section. `effect` is {impact,
// protection} from the API. Counts as they come; the missed orders are an estimate, so "about".
const n = (v) => Number(v).toLocaleString('en-GB');
export function effectRows(effect) {
  const impact = effect?.impact || {};
  const rows = [];
  if (impact.hidden_views != null) rows.push(['Your products hidden on partner sites', n(impact.hidden_views)]);
  if (effect?.protection?.protected_calls != null) {
    rows.push(["Partners' sale items hidden on your site", n(effect.protection.protected_calls)]);
  }
  if (impact.est_missed_orders != null) {
    const orders = impact.est_missed_orders;
    rows.push(['Orders from partners you probably missed', orders > 0 ? `about ${n(orders)}` : '0']);
  }
  return rows;
}
